import { Controller } from "@hotwired/stimulus"
import { setOptions, importLibrary } from "@googlemaps/js-api-loader"

let googleMapsOptionsSet = false

export default class extends Controller {
  static targets = [
    "autocompleteHost",
    "map",
    "lat",
    "lng",
    "placeName",
    "city",
    "countryCode",
    "address",
    "summary",
    "summaryName",
    "summaryMeta",
    "status",
  ]

  static values = {
    apiKey: String,
    country: String,
    lat: { type: Number, default: 14.5995 },
    lng: { type: Number, default: 120.9842 },
    readonly: { type: Boolean, default: false },
  }

  async connect() {
    if (!this.apiKeyValue) return

    try {
      if (!googleMapsOptionsSet) {
        setOptions({ key: this.apiKeyValue, v: "weekly" })
        googleMapsOptionsSet = true
      }

      await importLibrary("maps")
      if (!this.readonlyValue && this.hasAutocompleteHostTarget) {
        await importLibrary("places")
      }

      this.initMap()
      if (!this.readonlyValue && this.hasAutocompleteHostTarget) {
        await this.initAutocomplete()
      }

      this.renderSummaryFromFields()
      this.clearStatus()

      this.details = this.element.closest("details")
      if (this.details) {
        this.boundHandleDetailsToggle = this.handleDetailsToggle.bind(this)
        this.details.addEventListener("toggle", this.boundHandleDetailsToggle)
        if (this.details.open) this.resizeMap()
      }
    } catch (error) {
      console.error("Google Maps failed to load", error)
      this.showStatus(
        "Map search could not load. Enable Maps JavaScript API and Places API (New) in Google Cloud.",
      )
    }
  }

  disconnect() {
    if (this.details && this.boundHandleDetailsToggle) {
      this.details.removeEventListener("toggle", this.boundHandleDetailsToggle)
    }

    if (this.boundHandlePlaceSelect && this.placeAutocomplete) {
      this.placeAutocomplete.removeEventListener(
        "gmp-select",
        this.boundHandlePlaceSelect,
      )
    }

    this.placeAutocomplete?.remove()
  }

  handleDetailsToggle() {
    if (this.details?.open) this.resizeMap()
  }

  resizeMap() {
    if (!this.map || !this.marker) return

    const google = window.google
    if (!google?.maps) return

    google.maps.event.trigger(this.map, "resize")
    this.map.setCenter(this.marker.getPosition())
  }

  initMap() {
    const google = window.google
    const lat = this.latValue
    const lng = this.lngValue
    const center = { lat, lng }

    this.map = new google.maps.Map(this.mapTarget, {
      center,
      zoom: 15,
      disableDefaultUI: this.readonlyValue,
      gestureHandling: this.readonlyValue ? "cooperative" : "auto",
    })

    this.marker = new google.maps.Marker({ map: this.map, position: center })
  }

  async initAutocomplete() {
    const { PlaceAutocompleteElement } =
      await window.google.maps.importLibrary("places")

    const options = {
      placeholder: "Mall, street, landmark…",
    }

    if (this.countryValue) {
      options.includedRegionCodes = [this.countryValue.toLowerCase()]
    }

    this.placeAutocomplete = new PlaceAutocompleteElement(options)
    this.placeAutocomplete.className = "seller-location-row__place-search"
    this.placeAutocomplete.style.colorScheme = "light"

    this.boundHandlePlaceSelect = this.handlePlaceSelect.bind(this)
    this.placeAutocomplete.addEventListener(
      "gmp-select",
      this.boundHandlePlaceSelect,
    )

    this.autocompleteHostTarget.replaceChildren(this.placeAutocomplete)
  }

  async handlePlaceSelect(event) {
    const placePrediction = event.placePrediction
    if (!placePrediction) return

    try {
      const place = placePrediction.toPlace()
      await place.fetchFields({
        fields: [
          "displayName",
          "formattedAddress",
          "location",
          "addressComponents",
        ],
      })

      this.fillPlace(place)
    } catch (error) {
      console.error("Place details failed to load", error)
      this.showStatus("Could not load place details. Try another suggestion.")
    }
  }

  fillPlace(place) {
    const location = place.location
    if (!location) {
      this.showStatus("Pick a place from the suggestions list.")
      return
    }

    const lat =
      typeof location.lat === "function" ? location.lat() : location.lat
    const lng =
      typeof location.lng === "function" ? location.lng() : location.lng

    this.latTarget.value = lat
    this.lngTarget.value = lng
    this.placeNameTarget.value = place.displayName || ""
    if (this.hasAddressTarget) {
      this.addressTarget.value = place.formattedAddress || ""
    }

    const components = place.addressComponents || []
    const city =
      this.component(components, "locality") ||
      this.component(components, "administrative_area_level_2") ||
      this.component(components, "administrative_area_level_1")
    const country = this.component(components, "country", { short: true })

    if (this.hasCityTarget && city) this.cityTarget.value = city
    if (this.hasCountryCodeTarget && country) {
      this.countryCodeTarget.value = country
    }

    const center = { lat, lng }
    this.marker.setPosition(center)
    this.map.setCenter(center)
    this.renderSummaryFromFields()
    this.clearStatus()
  }

  renderSummaryFromFields() {
    if (!this.hasSummaryTarget) return

    const placeName = this.placeNameTarget?.value?.trim()
    const city = this.cityTarget?.value?.trim()
    const country = this.countryCodeTarget?.value?.trim()
    const address = this.addressTarget?.value?.trim()

    if (!placeName) {
      this.summaryTarget.classList.add("hidden")
      return
    }

    if (this.hasSummaryNameTarget) {
      this.summaryNameTarget.textContent = placeName
    }

    if (this.hasSummaryMetaTarget) {
      const meta = [city, country].filter(Boolean).join(", ")
      this.summaryMetaTarget.textContent =
        meta || address || "Location saved from map search"
    }

    this.summaryTarget.classList.remove("hidden")
  }

  showStatus(message) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.hidden = false
  }

  clearStatus() {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = ""
    this.statusTarget.hidden = true
  }

  component(components, type, { short = false } = {}) {
    const match = components.find((entry) => entry.types?.includes(type))
    if (!match) return ""

    if ("shortText" in match || "longText" in match) {
      return short ? match.shortText || "" : match.longText || ""
    }

    return short ? match.short_name || "" : match.long_name || ""
  }
}
